import { Component, EventEmitter, Input, OnInit, Output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TOOLS_BY_ID, ToolDef, toolImgUrl } from '../../tools/tools.data';
import { RoutineStoreService } from '../../tools/routine-store.service';
import { RoutineService } from '../../../services/routine.service';
import { RoutineSchedule, TOOL_BY_ID } from '../../../shell/shell.models';
import { AppDownloadCardComponent } from '../../../shared/components/app-download/app-download-card.component';
import { isWebDailyLoop } from '../../../services/daily-loop';

interface RTool extends ToolDef {
  img: string;
}

/** how many reinforcement lines to show in the "why these tools" block */
const WHY_MAX = 5;

/**
 * paid-0 — "Your tools".
 *
 * The closing panel of the premium results. By now the user has already chosen
 * her tools earlier in the results flow, so this page is the wrap-up: it
 * reflects the tools she chose (flat list, no scheduling / time-of-day split),
 * reinforces WHY each one helps, lets her drop a tool that doesn't fit, then
 * hands off to the app — where adding/changing the selection actually happens.
 *
 * Also surfaces the doctor-report entry point, important because the desktop
 * sidebar (its other entry point) is hidden on mobile.
 */
@Component({
  selector: 'app-routine-panel',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AppDownloadCardComponent],
  templateUrl: './routine-panel.component.html',
})
export class RoutinePanelComponent implements OnInit {
  /** Peritale web: no app, so the tools continue here (Danna 27.09). */
  readonly webLoop = isWebDailyLoop();
  /** user's first name for the personal greeting (empty → no-name fallback). */
  @Input() firstName = '';
  /** Paywall v2 (locked-flow spec §1): Option A locks the whole routine panel. */
  @Input() locked = false;
  /** The doctor summary (paid-4) embedded here stays locked for any unpaid
   *  account, even in Option B where the routine itself is free. */
  @Input() doctorLocked = false;
  /** emits when a locked surface is tapped → results paywall modal. */
  @Output() goPaywall = new EventEmitter<void>();

  private scanId = '';

  /** the chosen tools, decorated, in routine order — the store is the source of truth. */
  readonly tools = computed<RTool[]>(() =>
    this.store
      .tools()
      .map((id) => TOOLS_BY_ID[id])
      .filter(Boolean)
      .map((t) => ({ ...t, img: toolImgUrl(t.id) })),
  );

  /** one reinforcement line per chosen tool (capped), in routine order. */
  readonly whyKeys = computed<string[]>(() =>
    this.tools()
      .slice(0, WHY_MAX)
      .map((t) => t.insightKey),
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: RoutineStoreService,
    private routine: RoutineService,
  ) {}

  ngOnInit(): void {
    this.scanId = this.route.snapshot.params['scanId'] || '';
  }

  /** ngFor identity */
  trackTool = (_: number, t: RTool) => t.id;
  trackKey = (_: number, k: string) => k;

  get count(): number {
    return this.store.tools().length;
  }

  /** Dropping a tool here takes it out of her saved routine too, so the app
   *  never shows a tool she removed on this page. */
  async remove(id: string): Promise<void> {
    this.store.remove(id);
    try {
      const schedule: RoutineSchedule = { ...(await this.routine.getSchedule(true)) };
      if (schedule[id]?.active) {
        schedule[id] = { ...schedule[id], active: false };
        await this.routine.saveSchedule(schedule);
      }
    } catch (e) {
      console.error('remove from routine on results failed:', e);
    }
  }

  async openApp(): Promise<void> {
    // Persist the routine the user built here into the real schedule so the
    // chosen tools land under "what's left for today" on home (the hand-off
    // used to drop them — Danna 2026-06-18).
    try {
      const schedule: RoutineSchedule = { ...(await this.routine.getSchedule(true)) };
      let changed = false;
      for (const id of this.store.tools()) {
        if (TOOL_BY_ID[id] && !schedule[id]?.active) {
          schedule[id] = { active: true, freq: 'daily', time: 'anytime' };
          changed = true;
        }
      }
      if (changed) await this.routine.saveSchedule(schedule);
    } catch (e) {
      console.error('persist routine on app hand-off failed:', e);
    }
    // Land on HOME, not check-in: that's where the chosen tools surface under
    // "הנה מה שנשאר להיום" (BUG-H1, Danna 2026-06-18).
    this.router.navigate(['/app/home']);
  }

  openDoctorReport(): void {
    if (this.doctorLocked) {
      this.goPaywall.emit();
      return;
    }
    if (this.scanId) {
      this.router.navigate(['/app/doctor-report', this.scanId]);
    }
  }
}
